import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LuCalendarDays,
  LuHash,
  LuInfo,
  LuMail,
  LuMapPin,
  LuSparkles,
  LuSquarePen,
  LuTrash2,
  LuUmbrella,
} from 'react-icons/lu'
import { Box, Flex, Switch, Text } from '@chakra-ui/react'
import { AccessorySelectionType, OrderPaymentStatus, PickupStatus } from '@leid/api-orders'
import Link from 'next/link'

import ProductInfoButtons from 'app/components/shared/ProductInfoButtons'
import CameraModal from 'components/partials/rentalsHistory/RentalsHistorySection/components/Camera/CameraModal'
import { removeDecimals } from 'utils/removeDecimals'

import { ItemCardAccessoryItemAmount } from './components/ItemCardAccessoryItemAmount'
import ProductImageView from './components/ItemCardImage'
import { ItemCardListItem } from './components/ItemCardListItem'
import { ItemCardPriceItem } from './components/ItemCardPriceItem'
import { formatItemCardDateDate } from './helpers'
import { ItemCardProps } from './interfaces'
import {
  ItemCardActionButtonsContainer,
  ItemCardActions,
  ItemCardDeleteButton,
  ItemCardDetails,
  ItemCardDisabledSpan,
  ItemCardEditButton,
  ItemCardSecondarySpan,
  ItemCardText,
  ItemCardThumbnail,
  ItemCardWrapper,
} from './styles'

export const ItemCard: React.FC<ItemCardProps> = ({
  orderItemInfo,
  footer,
  onDecreaseAmount,
  onIncreaseAmount,
  onToggleDeductibles,
  onDelete,
  editHref,
  editAs,
  imageWidth = 88,
  imageHeight = 88,
  bundleProductImageWidth = 70,
  bundleProductImageHeight = 70,
  hideInsuranceAndDeductiblesPriceIfResigned,
  hideExtraInfoButtons,
  ...boxProps
}) => {
  const { t } = useTranslation(['common', 'statuses'])
  const { orderItem, manager, reconciliations } = orderItemInfo
  const {
    rentedFrom,
    rentedTo,
    amount,
    resignFromInsurance,
    resignFromDeductibles,
    status,
    productData,
    messageToCustomer,
  } = orderItem
  const isConsumable = orderItem.productData?.complementary ?? false
  const isAccessory = orderItem.associatedParentOrderItemId !== undefined
  const isPaymentStarted = status !== OrderPaymentStatus.started

  const [renderCameraModal, setRenderCameraModal] = useState(false)
  const [disableDeleteButton, setDisableDeleteButton] = useState(false)
  const [accessorySelectionType, setAccessorySelectionType] = useState<AccessorySelectionType | undefined>(undefined)

  useEffect(() => {
    shouldRenderCameraModal()
  }, [])

  const shouldRenderCameraModal = () => {
    if (!orderItemInfo.orderItem) {
      return
    }
    // Copy variable without reference
    const orderItemCopy = JSON.parse(JSON.stringify(orderItemInfo.orderItem))
    const { paymentStatus } = orderItemCopy
    const { rentedFrom, rentedTo } = orderItemCopy
    const rentedFromMinusOneHour = new Date(rentedFrom)
    const rentedToPlusTwoWeeks = new Date(rentedTo)
    const now = new Date()

    if (paymentStatus !== OrderPaymentStatus.paid) {
      return
    }

    if (!rentedFromMinusOneHour || !rentedToPlusTwoWeeks) {
      return
    }
    rentedFromMinusOneHour.setHours(rentedFromMinusOneHour.getHours() - 1)
    rentedToPlusTwoWeeks.setDate(rentedToPlusTwoWeeks.getDate() + 14)
    rentedToPlusTwoWeeks.setHours(rentedToPlusTwoWeeks.getHours() + 1)

    if (now < rentedFromMinusOneHour || now > rentedToPlusTwoWeeks) {
      return
    }
    setRenderCameraModal(true)
  }

  const insurancePrice = orderItem.insurancePrice ?? orderItem.productData?.insurancePrice ?? 0
  const deductiblesPrice = orderItem.deductiblesPrice ?? orderItem.productData?.deductiblesPrice ?? 0
  const deductiblesAmount = orderItem.deductiblesAmount ?? orderItem.productData?.deductiblesAmount ?? 0

  const handleDelete = useCallback(() => {
    if (onDelete) {
      setDisableDeleteButton(true)

      try {
        onDelete()
      } catch (e) {
        setDisableDeleteButton(false)
      }
    }
  }, [])

  const hasInsurancePercentage = () => {
    if (!productData) {
      return false
    }

    if (productData.insurancePercentage === undefined || productData.insurancePercentage === 0) {
      return false
    }

    return true
  }

  const hasFixedInsurance = () => {
    if (!productData) {
      return false
    }

    if (productData.insurancePrice === undefined || productData.insurancePrice === 0) {
      return false
    }

    return true
  }

  const hideInsuranceBlock = () => {
    const insurancePercentage = hasInsurancePercentage()

    const insuranceFixed = hasFixedInsurance()

    if (!insurancePercentage && !insuranceFixed) {
      return true
    }

    return false
  }

  const hideDeductiblesPriceBlock = () => {
    if (orderItem.productData?.deductiblesAmount === 0) {
      return true
    }

    const deductiblesPrice = orderItem.productData?.deductiblesPrice ?? 0
    return deductiblesPrice === 0
  }

  const itemName = orderItem.productData?.name ?? ''

  const products = orderItem.productData?.products
  if (!products) {
    return null
  }

  const isBundle = products.length >= 2
  const singleProduct = !isBundle ? products[0] : undefined
  const isReturned = [PickupStatus.RETURNED, PickupStatus.INSPECTED, PickupStatus.COMPLETED].includes(
    orderItem.pickupStatus,
  )

  return (
    <Box {...boxProps}>
      <ItemCardWrapper isBundle={isBundle}>
        <ItemCardDetails>
          <Box marginRight="auto">
            <ItemCardText fontSize="md" isBundle={isBundle} marginBottom="10px">
              {itemName}
            </ItemCardText>
            {(status === OrderPaymentStatus.canceled || status === OrderPaymentStatus.paidAndCanceled) && (
              <Text fontSize="xs" marginBottom="10px" color="red.a150">
                {t('statuses:canceled')}
              </Text>
            )}
            {orderItemInfo.location && (
              <ItemCardListItem luIcon={<LuMapPin size={12} />} isBundle={isBundle} fontWeight={700}>
                {orderItemInfo.location.name}
                <Text fontSize="xs">
                  <ItemCardSecondarySpan isBundle={isBundle}>{orderItemInfo.location.address}</ItemCardSecondarySpan>
                </Text>
              </ItemCardListItem>
            )}
            <ItemCardPriceItem orderItem={orderItem} isBundle={isBundle} />

            {isAccessory && (
              <ItemCardAccessoryItemAmount
                amount={amount}
                orderItemInfo={orderItemInfo}
                isBundle={isBundle}
                isConsumable={isConsumable}
                isPaymentStarted={isPaymentStarted}
                onDecreaseAmount={onDecreaseAmount}
                onIncreaseAmount={onIncreaseAmount}
                setAccessorySelectionType={setAccessorySelectionType}
              />
            )}

            {rentedFrom && (
              <ItemCardListItem luIcon={<LuCalendarDays size={12} />} isBundle={isBundle}>
                <ItemCardSecondarySpan isBundle={isBundle}>{t('common:pickup')}: </ItemCardSecondarySpan>
                {formatItemCardDateDate(rentedFrom)}
              </ItemCardListItem>
            )}
            {rentedTo && (
              <ItemCardListItem luIcon={<LuCalendarDays size={12} />} isBundle={isBundle}>
                <ItemCardSecondarySpan isBundle={isBundle}>{t('common:return')}: </ItemCardSecondarySpan>
                {formatItemCardDateDate(rentedTo)}
              </ItemCardListItem>
            )}

            {amount !== 1 && (
              <ItemCardListItem luIcon={<LuHash size={12} />} isBundle={isBundle}>
                <ItemCardSecondarySpan isBundle={isBundle}>{t('common:Amount')}: </ItemCardSecondarySpan>
                {amount}
              </ItemCardListItem>
            )}

            {messageToCustomer && (
              <ItemCardListItem luIcon={<LuMail size={12} />} isBundle={isBundle}>
                <ItemCardSecondarySpan isBundle={isBundle}>{t('common:messageToCustomer')}: </ItemCardSecondarySpan>
                {messageToCustomer}
              </ItemCardListItem>
            )}
            {!isConsumable && !hideInsuranceBlock() && (
              <ItemCardListItem luIcon={<LuUmbrella size={12} />} isBundle={isBundle}>
                <ItemCardSecondarySpan isBundle={isBundle}>{t('common:insurance')}: </ItemCardSecondarySpan>
                {hideInsuranceAndDeductiblesPriceIfResigned && resignFromInsurance ? (
                  <ItemCardDisabledSpan isBundle={isBundle}>{t('common:notIncluded')}</ItemCardDisabledSpan>
                ) : (
                  `${removeDecimals(insurancePrice)} ${t('common:nok')}`
                )}
              </ItemCardListItem>
            )}
            {!isConsumable && !hideDeductiblesPriceBlock() && (
              <ItemCardListItem luIcon={<LuSparkles size={12} />} isBundle={isBundle}>
                <Flex flexDirection="row" alignItems="center">
                  <Box>
                    <Text fontSize="xs">
                      <ItemCardSecondarySpan isBundle={isBundle}>
                        {deductiblesAmount === undefined && resignFromDeductibles
                          ? t('standardDeductibles')
                          : `${t('deductible')} ${!resignFromDeductibles ? '0' : removeDecimals(deductiblesAmount)} ${t(
                              'NOK',
                            )}`}
                      </ItemCardSecondarySpan>
                    </Text>
                    <ItemCardSecondarySpan isBundle={isBundle}>
                      {t('common:reductionOfDeductible')}:{' '}
                    </ItemCardSecondarySpan>
                    {hideInsuranceAndDeductiblesPriceIfResigned && resignFromDeductibles ? (
                      <ItemCardDisabledSpan isBundle={isBundle}>{t('common:notIncluded')}</ItemCardDisabledSpan>
                    ) : (
                      `${removeDecimals(deductiblesPrice)} ${t('common:nok')}`
                    )}
                  </Box>
                  {onToggleDeductibles && (
                    <Box>
                      <Switch.Root
                        size="lg"
                        colorPalette="leidTheme"
                        onCheckedChange={onToggleDeductibles}
                        checked={!resignFromDeductibles}
                        defaultChecked={!resignFromDeductibles}
                        disabled={isPaymentStarted}
                        marginLeft="8px"
                        marginTop="-4px"
                      >
                        <Switch.HiddenInput />
                        <Switch.Control />
                      </Switch.Root>
                    </Box>
                  )}
                </Flex>
              </ItemCardListItem>
            )}

            {manager && manager.terms && (
              <ItemCardListItem luIcon={<LuInfo size={12} />} isBundle={isBundle}>
                <Link href={manager.terms} passHref legacyBehavior>
                  <a target="_blank" rel="noopener noreferrer">
                    <Text as="span" color="green.a200" fontWeight={600} cursor="pointer" fontSize="sm" lineHeight="1">
                      {t('common:termsOfUse')}
                    </Text>
                  </a>
                </Link>
              </ItemCardListItem>
            )}

            {reconciliations &&
              reconciliations.length !== 0 &&
              reconciliations
                .filter((s) => s.reconciliationDescription)
                .map((s) => (
                  <ItemCardListItem luIcon={<LuMail size={12} />} key={s.id} isBundle={isBundle}>
                    {s.reconciliationDescription}
                  </ItemCardListItem>
                ))}
          </Box>

          <ItemCardActions>
            {singleProduct && singleProduct.imageUrl && (
              //   <ItemCardThumbnail
              //     src={singleProduct.imageUrl}
              //     width={imageWidth}
              //     minWidth={imageWidth}
              //     height={imageHeight}
              //   />

              <ProductImageView
                productDefaultImage={singleProduct.imageUrl}
                imageVersions={singleProduct.imageVersions ?? []}
                desiredVersion="thumbnail"
                altText={singleProduct.name}
                htmlWidth={`${imageWidth}px`}
                htmlHeight={`${imageHeight}px`}
              />
            )}
            {renderCameraModal && <CameraModal orderItem={orderItem} />}

            {!isPaymentStarted && (
              <ItemCardActionButtonsContainer>
                {onDelete && accessorySelectionType !== AccessorySelectionType.MANDATORY && (
                  <ItemCardDeleteButton isDisabled={disableDeleteButton} onClick={handleDelete}>
                    <LuTrash2 />
                  </ItemCardDeleteButton>
                )}
                {editHref && (
                  <ItemCardEditButton href={editAs}>
                    <LuSquarePen />
                  </ItemCardEditButton>
                )}
              </ItemCardActionButtonsContainer>
            )}
          </ItemCardActions>
        </ItemCardDetails>
        {isBundle && (
          <Box marginTop="20px" borderTop="1px solid {colors.green.a175}">
            {products.map((product) => (
              <Flex alignItems="center" marginTop="10px" key={product.id}>
                {product.imageUrl && (
                  <ItemCardThumbnail
                    src={product.imageUrl}
                    width={bundleProductImageWidth}
                    minWidth={bundleProductImageWidth}
                    height={bundleProductImageHeight}
                  />
                )}
                <Text fontSize="sm" fontWeight={700} marginLeft="20px" color="white">
                  {product.name}
                </Text>
              </Flex>
            ))}
          </Box>
        )}

        {!hideExtraInfoButtons && !isReturned && (
          <Box marginTop="15px">
            <ProductInfoButtons
              masterArticle={orderItemInfo.masterArticle}
              relatedArticle={orderItemInfo.relatedArticle}
              location={orderItemInfo.location}
            />
          </Box>
        )}

        {footer}
      </ItemCardWrapper>
    </Box>
  )
}
